/* Scandinavian Edition — colour picker on product pages.
   Each colour is its own product, so Swatch King's colour row links between products. This replaces
   that row with tiles showing the front packshot ("_plp") of each colour, laid out on the same grid
   as the size buttons (one column per size) so colour and size line up. Swatch King still handles sizes.
   Data: /search?view=se-siblings (templates/search.se-siblings.liquid), cached for the visit. */
(() => {
  const cfg = window.SE_PRODUCT;
  if (!cfg || !cfg.title) return;
  const split = (t) => {
    const parts = String(t).split(' — ');
    return { model: parts[0].trim(), color: (parts[1] || '').trim() };
  };
  const me = split(cfg.title);
  if (!me.model) return;

  /* Cached for 15 minutes per tab, so a new colour or an index hiccup never sticks for a whole visit.
     One retry before giving up, since a failed fetch hands the row back to Swatch King. */
  const load = () => {
    const key = 'se-colors-v2-' + (document.documentElement.lang || '') + '-' + me.model;
    try {
      const hit = JSON.parse(sessionStorage.getItem(key));
      if (hit && hit.t > Date.now() - 15 * 60 * 1000 && hit.list && hit.list.length) return Promise.resolve(hit.list);
    } catch (e) {}
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}search?q=${encodeURIComponent(me.model)}&type=product&options[prefix]=last&view=se-siblings`;
    const get = () => fetch(url, { credentials: 'same-origin' }).then((r) => {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    });
    return get()
      .catch(() => new Promise((ok) => setTimeout(ok, 600)).then(get))
      .then((list) => {
        const out = list
          .map((p) => ({ ...p, ...split(p.title) }))
          .filter((p) => p.model === me.model && p.color)
          .sort((a, b) => a.color.localeCompare(b.color));
        if (out.length) {
          try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), list: out })); } catch (e) {}
        }
        return out;
      });
  };

  /* Solid colour for the phone chips: the median colour of the garment in a small copy of the
     packshot (same rule as Weather Picks). Studio backdrop pixels (sampled from the corner) are
     ignored, and the median keeps zips, buttons and seams from skewing it. */
  const swatchColour = (src) => new Promise((resolve) => {
    if (!src) return resolve(null);
    const key = 'se-col-' + src.split('?')[0];
    try {
      const hit = sessionStorage.getItem(key);
      if (hit) return resolve(hit);
    } catch (e) {}
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const w = 60;
        const h = Math.round((img.naturalHeight / img.naturalWidth) * w) || 90;
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        const bg = [data[8], data[9], data[10]];
        const r = [], g = [], b = [];
        for (let y = Math.floor(h * 0.2); y < h * 0.8; y++) {
          for (let x = Math.floor(w * 0.15); x < w * 0.85; x++) {
            const i = (y * w + x) * 4;
            const d = Math.abs(data[i] - bg[0]) + Math.abs(data[i + 1] - bg[1]) + Math.abs(data[i + 2] - bg[2]);
            if (d < 24 || data[i + 3] < 200) continue;
            r.push(data[i]); g.push(data[i + 1]); b.push(data[i + 2]);
          }
        }
        if (r.length < 20) return resolve(null);
        const med = (a) => a.sort((m, n) => m - n)[a.length >> 1];
        const col = `rgb(${med(r)}, ${med(g)}, ${med(b)})`;
        try { sessionStorage.setItem(key, col); } catch (e) {}
        resolve(col);
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src.replace(/([?&])width=\d+/, '$1width=120');
  });

  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* The colour row sits above Swatch King's size row. On products where Swatch King does not render,
     it sits above Fabric's own size picker instead, and moves to Swatch King if that shows up later. */
  let box = null;
  let anchorEl = null;
  const sizeCount = (el) =>
    el.querySelectorAll('.swatch-view-button .swatch-view-item').length ||
    el.querySelectorAll('.variant-option__button-label').length;

  const mount = (sk) => {
    if (sk.dataset.seColors) return;
    sk.dataset.seColors = '1';
    if (box) {
      /* Already built (above Fabric's picker): move it */
      if (anchorEl) anchorEl.classList.remove('se-colors-on');
      sk.parentNode.insertBefore(box, sk);
      sk.classList.add('se-colors-on');
      anchorEl = sk;
      const sync = () => {
        if (anchorEl !== sk) return;
        const cols = Math.max(4, Math.min(sizeCount(sk) || 6, 7));
        box.style.setProperty('--se-cols', cols);
        box.style.setProperty('--se-cols-2', cols * 2);
        sk.style.setProperty('--se-cols', cols);
      };
      sync();
      new MutationObserver(sync).observe(sk, { childList: true, subtree: true });
      return;
    }
    anchorEl = sk;
    box = document.createElement('div');
    box.className = 'se-colors';
    box.innerHTML =
      `<p class="se-colors__label">${esc(cfg.colorLabel || 'Color')}<span class="se-colors__value">${esc(me.color)}</span></p>` +
      `<ul class="se-colors__grid" role="list"><li class="se-colors__tile se-colors__tile--ghost" aria-hidden="true"></li></ul>`;
    sk.parentNode.insertBefore(box, sk);
    sk.classList.add('se-colors-on');

    /* One column per size, so the two rows share a grid */
    const syncCols = () => {
      if (anchorEl !== sk) return;
      const n = sizeCount(sk);
      const cols = Math.max(4, Math.min(n || 6, 7));
      box.style.setProperty('--se-cols', cols);
      box.style.setProperty('--se-cols-2', cols * 2);
      sk.style.setProperty('--se-cols', cols);
    };
    syncCols();
    new MutationObserver(syncCols).observe(sk, { childList: true, subtree: true });

    load()
      .then((list) => {
        if (!list.length) throw new Error('none');
        box.querySelector('.se-colors__grid').innerHTML = list
          .map((p) => {
            const cur = p.color === me.color;
            const cls = 'se-colors__tile' + (cur ? ' is-current' : '') + (p.available ? '' : ' is-soldout');
            const img = p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy" width="120" height="180">` : '';
            return `<li class="${cls}"><a href="${esc(p.url)}" aria-label="${esc(p.color)}${p.available ? '' : ` (${cfg.soldOut || 'sold out'})`}" title="${esc(p.color)}"${cur ? ' aria-current="true"' : ''}>${img}</a></li>`;
          })
          .join('');
        /* Phone chips show a flat colour; the photo stays as fallback */
        box.querySelectorAll('.se-colors__tile img').forEach((img) => {
          swatchColour(img.getAttribute('src')).then((col) => {
            if (!col) return;
            const tile = img.closest('.se-colors__tile');
            tile.style.setProperty('--se-chip', col);
            tile.classList.add('has-chip');
          });
        });
      })
      .catch(() => {
        /* Something failed: give the colour row back to Swatch King */
        box.remove();
        if (anchorEl) anchorEl.classList.remove('se-colors-on');
      });
  };

  const inMain = (el) => !el.closest('dialog, quick-add-dialog, .quick-add, product-card');
  const find = () => {
    const sks = Array.from(document.querySelectorAll('variant-swatch-king')).filter(inMain);
    if (sks.length) { sks.forEach(mount); return; }
    if (box) return;
    const vp = Array.from(document.querySelectorAll('variant-picker')).filter(inMain)[0];
    if (vp) mount(vp);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', find, { once: true });
  else find();
  new MutationObserver(find).observe(document.documentElement, { childList: true, subtree: true });
})();
